/**
 * Sanity: the horror stat.
 *
 * Darkness bleeds it slowly, entities take it in chunks, and only a working
 * ceiling light gives any of it back. A flashlight holds the dark off but
 * repairs nothing — being alone with your own beam is not comfort, it is just
 * a smaller room.
 */

import { TUNING } from '../core/config';
import { clamp01, smoothstep } from '../core/math';
import type { EntityKind, EntityState } from '../core/types';

/**
 * Per-kind drain per second at contact range, before the visibility scale.
 * Mirrors `EntityDef.sanityDrain`, which lives in `entities/` and cannot be
 * imported here — subsystems talk through core, not to each other.
 *
 * These are the *passive* floor: what merely being near one costs, whether or
 * not its behaviour has anything to say. A behaviour that wants more reports it
 * as `entity:dread`, and the survival system takes the worse of the two, so a
 * number here is never paid twice.
 *
 * Roughly half the matching `sanityDrain` throughout, and ordered the same way:
 * the things that stand and are looked at cost the most per second of being
 * looked at, and the things that run at you do their damage in health.
 */
const ENTITY_PRESSURE: Record<EntityKind, number> = {
  // The two that are worst to look at rather than worst to meet.
  faceling: 2.4,
  smiler: 2.2,
  partygoer: 2.1,
  bacteria: 1.8,
  hound: 1.6,
  centipede: 1.3,
  hollow: 1.1,
  deathmoth: 0.9,
  clump: 0.35,
};

/** Metres at which an entity stops mattering. Seeing one reaches much further. */
const SEEN_RANGE = 22;
const UNSEEN_RANGE = 11;

/**
 * How much light the player's own beam is worth to them, 0..1. Deliberately
 * short of the threshold where the dark stops costing anything: a torch makes
 * the corridor survivable, not safe.
 */
const BEAM_LIGHT = 0.34;

/**
 * Sanity drained per second by whatever is near. The worst single entity wins
 * rather than the sum: a pack should be terrifying, not instantly fatal.
 */
export function entityPressure(entities: readonly EntityState[]): number {
  let worst = 0;
  for (const e of entities) {
    if (e.mode === 'dead' || e.mode === 'dormant') continue;
    const range = e.visible ? SEEN_RANGE : UNSEEN_RANGE;
    if (e.distToPlayer >= range) continue;
    let drain = (ENTITY_PRESSURE[e.kind] ?? 1) * (1 - smoothstep(range * 0.2, range, e.distToPlayer));
    if (e.visible) drain *= TUNING.survival.sanityVisibleScale;
    if (drain > worst) worst = drain;
  }
  return worst;
}

/** Room light plus the player's own beam, which fades as the cells die. */
export function effectiveLight(roomLight: number, flashlightOn: boolean, charge: number): number {
  const beam = flashlightOn && charge > 0 ? BEAM_LIGHT * clamp01(charge / TUNING.flashlight.dyingThreshold) : 0;
  return Math.max(clamp01(roomLight), beam);
}

/** Sanity lost per second to the dark. */
export function darknessDrain(light: number): number {
  return TUNING.survival.sanityDarkRate * (1 - smoothstep(0.12, 0.5, light));
}

/**
 * Sanity regained per second. Only real room light counts, and standing still
 * under it counts for more — the game rewards stopping, which is the last thing
 * a lost person wants to do.
 */
export function litRecovery(roomLight: number, resting: boolean): number {
  const lit = smoothstep(0.3, 0.62, roomLight);
  if (lit <= 0) return 0;
  return (resting ? TUNING.survival.sanityRestRecovery : TUNING.survival.sanityLitRecovery) * lit;
}
